import { memo } from 'react';
import { DollarSign, TrendingUp, Target, BarChart3 } from 'lucide-react';

const ChannelCard = ({ channel }) => {
  const getCategoryColor = (category) => {
    switch (category) {
      case 'paid':
        return 'bg-purple-50 border-purple-200 text-purple-700';
      case 'organic':
        return 'bg-green-50 border-green-200 text-green-700';
      case 'email':
        return 'bg-blue-50 border-blue-200 text-blue-700';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-700';
    }
  };

  const roasColor = channel.roas >= 5 ? 'text-green-600' : channel.roas >= 3 ? 'text-yellow-600' : 'text-red-600';
  const roasBg = channel.roas >= 5 ? 'bg-green-50' : channel.roas >= 3 ? 'bg-yellow-50' : 'bg-red-50';

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-gray-900">{channel.name}</h4>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium border ${getCategoryColor(channel.category)}`}>
            {channel.category.charAt(0).toUpperCase() + channel.category.slice(1)}
          </span>
        </div>
        <div className={`px-4 py-2 rounded-lg ${roasBg}`}>
          <p className="text-xs text-gray-600 font-medium">ROAS</p>
          <p className={`text-2xl font-bold ${roasColor}`}>
            {channel.roas.toFixed(1)}x
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <p className="text-xs text-gray-600">Ad Spend</p>
          <p className="text-lg font-semibold text-gray-900">
            ${channel.adSpend.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Revenue</p>
          <p className="text-lg font-semibold text-gray-900">
            ${channel.revenue.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Leads</p>
          <p className="text-lg font-semibold text-gray-900">
            {channel.leads}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">Conversions</p>
          <p className="text-lg font-semibold text-gray-900">
            {channel.conversions}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">CPA</p>
          <p className="text-sm font-medium text-gray-700">
            ${channel.cpa.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-600">CPL</p>
          <p className="text-sm font-medium text-gray-700">
            ${channel.cpl.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Progress bar for ad spend allocation */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-600 mb-1">
          <span>Budget Allocation</span>
          <span>{((channel.adSpend / 58000) * 100).toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-500 h-2 rounded-full transition-all"
            style={{ width: `${(channel.adSpend / 58000) * 100}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, subtitle, icon: Icon, trend }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        {subtitle && (
          <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        )}
      </div>
      {Icon && (
        <div className="p-3 bg-blue-50 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      )}
    </div>
  </div>
);

export const ROASDistribution = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading ROAS distribution data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { channels, summary } = data;

  // Sort channels by ROAS descending
  const sortedChannels = [...channels].sort((a, b) => b.roas - a.roas);

  return (
    <div className="space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Ad Spend"
          value={`$${summary.totalAdSpend.toLocaleString()}`}
          icon={DollarSign}
        />
        <SummaryCard
          label="Total Revenue"
          value={`$${summary.totalRevenue.toLocaleString()}`}
          icon={TrendingUp}
        />
        <SummaryCard
          label="Average ROAS"
          value={`${summary.averageROAS.toFixed(2)}x`}
          subtitle="Return on Ad Spend"
          icon={BarChart3}
        />
        <SummaryCard
          label="Total Conversions"
          value={summary.totalConversions}
          subtitle={`from ${summary.totalLeads} leads`}
          icon={Target}
        />
      </div>

      {/* Channel Performance Grid */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Channel Performance Breakdown
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedChannels.map((channel, index) => (
            <ChannelCard key={index} channel={channel} />
          ))}
        </div>
      </div>

      {/* Ad Spend Allocation Visualization */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Ad Spend Allocation</h4>
        <div className="space-y-3">
          {sortedChannels.map((channel, index) => {
            const percentage = (channel.adSpend / summary.totalAdSpend) * 100;
            return (
              <div key={index} className="flex items-center gap-4">
                <div className="w-32 text-sm font-medium text-gray-700">
                  {channel.name}
                </div>
                <div className="flex-1">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-6 rounded-full flex items-center justify-end pr-2 transition-all"
                      style={{ width: `${percentage}%` }}
                    >
                      <span className="text-xs text-white font-medium">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="w-24 text-right text-sm font-semibold text-gray-900">
                  ${channel.adSpend.toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Insights */}
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Performance Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Best ROAS:</span>{' '}
              {sortedChannels[0]?.name} at {sortedChannels[0]?.roas.toFixed(1)}x
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Highest Spend:</span>{' '}
              {channels.reduce((max, ch) => ch.adSpend > max.adSpend ? ch : max)?.name} at $
              {channels.reduce((max, ch) => ch.adSpend > max.adSpend ? ch : max)?.adSpend.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Most Conversions:</span>{' '}
              {channels.reduce((max, ch) => ch.conversions > max.conversions ? ch : max)?.name} with{' '}
              {channels.reduce((max, ch) => ch.conversions > max.conversions ? ch : max)?.conversions} deals
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Lowest CPA:</span>{' '}
              {channels.reduce((min, ch) => ch.cpa < min.cpa ? ch : min)?.name} at $
              {channels.reduce((min, ch) => ch.cpa < min.cpa ? ch : min)?.cpa.toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

ROASDistribution.displayName = 'ROASDistribution';